import React, { Suspense, lazy } from "react";
import Intro from "../Components/Intro";
import Heading from "../Components/Heading";
import CallAction from "../Components/CallAction";
import Trustedby from "../Components/Trustedby";
import Contact from "../Components/Contact";
import FAQ from "../Components/FAQ";
import SEO from "../Components/SEO";
import { motion } from "framer-motion";
import ServicesCards from '../Components/ServicesCards';

const LazyLoadedComponent = lazy(() => import("../Components/IndustriesCards"));

const homePageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "ElectroPlating Lebanon - Premium Metal Finishing Services",
  "description": "Professional electroplating and metal finishing services in Lebanon since 1998. Chrome, gold, silver, and nickel plating for automotive, jewelry, and industrial applications.",
  "url": "https://www.electroplating-lb.com",
  "mainEntity": {
    "@type": "LocalBusiness",
    "name": "ElectroPlating Lebanon",
    "telephone": "+961-03-518-323",
    "email": "electroplatinglb@gmail.com"
  }
};

// Animation variants for scroll-triggered animations
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.8, ease: "easeOut" }
  },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  },
};

const HomePage = () => {
  return (
    <main>
      <SEO
        title="Premium Metal Finishing Services Since 1998"
        description="ElectroPlating Lebanon offers professional electroplating and metal finishing services including chrome, gold, silver, and nickel plating. Serving automotive, jewelry, furniture, and industrial sectors since 1998."
        keywords="electroplating Lebanon, metal finishing, chrome plating, gold plating, silver plating, nickel plating, automotive plating, jewelry plating"
        canonicalUrl="/"
        structuredData={homePageSchema}
      />

      {/* Hero - Immediate fade in */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <Heading />
      </motion.div>

      {/* Intro - Scroll triggered */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInUp}
      >
        <Intro />
      </motion.div>

      {/* Call to Action - Scroll triggered slide from left */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={slideInLeft}
      >
        <CallAction />
      </motion.div>

      {/* Services - Scroll triggered with stagger */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInUp}
      >
        <ServicesCards />
      </motion.div>

      {/* Industries - Scroll triggered */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInUp}
      >
        <Suspense fallback={<div style={{ minHeight: '400px' }}></div>}>
          <LazyLoadedComponent />
        </Suspense>
      </motion.div>

      {/* Trusted By - Scroll triggered fade */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeIn}
      >
        <Trustedby />
      </motion.div>

      {/* Contact - Scroll triggered */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        <Contact />
      </motion.div>

      {/* FAQ - Scroll triggered */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        <FAQ />
      </motion.div>
    </main>
  );
};

const MemoizedHomePage = React.memo(HomePage);

export default MemoizedHomePage;
